import React, { useEffect, useMemo, useState } from "react";
import LoadingButton from "../LoadingButton";
import PhotoUploadForm from "../../../../../modals/forms/PhotoForm/photoUploadForm";
import PhotoListForm from "../../../../../modals/forms/PhotoForm/photolistFormSmall";
import { FIELD_LABELS } from "../../../../../../constants/summaryTabFieldLables";
import { FIELD_PLACEHOLDERS } from "../../../../../../constants/summaryTabFieldplaceHolder";
import { isQaQcManager } from "../../../../../../constants/projectRoles";
import { inspectionsAPI } from "../../../../../../services/api";

const norm = (v) => (v ?? "").toString();
const isDraftLike = (s) => (s || "").toLowerCase() === "draft";
const fieldLabel = (key) => {
  if (key === "site_location") return "Site location";
  if (key === "location") return "Location";
  return FIELD_LABELS[key] || key;
};
const fieldPlaceholder = (key) => {
  if (key === "site_location") return "e.g. Level 2 – Electrical Room, Area B";
  return FIELD_PLACEHOLDERS[key] || "";
};

const SummaryTab = ({
  detail,
  detailLoading,
  editSummary,
  setEditSummary,
  onSaveSummary,
  savingSummary,
  photosForInspection = [],
  photosLoading,
  photosError,
  removePhoto,
  photoMeta,
  onPhotoMetaChange,
  photoFile,
  setPhotoFile,
  onSubmitPhoto,
  uploadingPhoto,
  inspectionTypes = [],
  inspectionTypesLoading = false,
  pdfFile,
  setPdfFile,
  onSubmitInspectionPdf,
  pdfUploading,
  inspectionPdfs = [],
  availableInspectionPdfs = [],
  inspectionPdfsLoading = false,
  linkingInspectionPdfId = null,
  onLinkExistingInspectionPdf,
  onUnlinkInspectionPdf,
  projectId,
  projectRole,
}) => {
  const summaryEditable =
    !!detail &&
    (isDraftLike(detail.status) ||
      ((detail.status || "").toLowerCase() === "submitted" &&
        isQaQcManager(projectRole)));

  const [typesExpanded, setTypesExpanded] = useState(false);
  const [typeSearch, setTypeSearch] = useState("");
  const [existingPdfId, setExistingPdfId] = useState("");
  const [qaQcModifications, setQaQcModifications] = useState([]);
  const [qaQcAuditLoading, setQaQcAuditLoading] = useState(false);
  const [qaQcAuditOpen, setQaQcAuditOpen] = useState(false);
  const [qaQcAuditRefreshKey, setQaQcAuditRefreshKey] = useState(0);
  const [summarySaveError, setSummarySaveError] = useState("");
  const [summarySaved, setSummarySaved] = useState(false);

  useEffect(() => {
    setExistingPdfId("");
  }, [detail?.id]);

  useEffect(() => {
    let cancelled = false;
    const loadAudit = async () => {
      if (!projectId || !detail?.id) {
        setQaQcModifications([]);
        return;
      }
      setQaQcAuditLoading(true);
      try {
        const res = await inspectionsAPI.listQaQcModifications(
          projectId,
          detail.id,
        );
        if (!cancelled) {
          setQaQcModifications(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        // Audit visibility is role-scoped; keep Summary usable if it is unavailable.
        if (!cancelled) setQaQcModifications([]);
      } finally {
        if (!cancelled) setQaQcAuditLoading(false);
      }
    };
    loadAudit();
    return () => {
      cancelled = true;
    };
  }, [projectId, detail?.id, detail?.updated_at, qaQcAuditRefreshKey]);

  const handleLinkExistingPdf = async (e) => {
    e.preventDefault();
    if (!existingPdfId || !onLinkExistingInspectionPdf) return;

    await onLinkExistingInspectionPdf(Number(existingPdfId));
    setExistingPdfId("");
  };

  const selectedTypeIds = useMemo(
    () => editSummary?.inspection_type_ids ?? [],
    [editSummary?.inspection_type_ids],
  );

  const selectedTypes = useMemo(
    () => (inspectionTypes || []).filter((t) => selectedTypeIds.includes(t.id)),
    [inspectionTypes, selectedTypeIds],
  );

  const filteredTypes = useMemo(() => {
    const q = typeSearch.trim().toLowerCase();
    const list = inspectionTypes || [];

    if (!q) return list;

    return list.filter((t) => {
      const name = (t.name || "").toLowerCase();
      const desc = (t.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [inspectionTypes, typeSearch]);

  const toggleTypeId = (id) => {
    if (!summaryEditable) return;

    setEditSummary((prev) => {
      const prevIds = prev?.inspection_type_ids || [];
      const exists = prevIds.includes(id);

      const nextIds = exists
        ? prevIds.filter((x) => x !== id)
        : [...prevIds, id];

      const allTypes = inspectionTypes || [];
      const nextSelected = allTypes.filter((t) => nextIds.includes(t.id));
      const label = nextSelected.map((t) => t.name).join(", ");

      return {
        ...prev,
        inspection_type_ids: nextIds,
        inspection_type: label,
      };
    });
  };

  const summaryDirty = useMemo(() => {
    if (!detail) return false;

    const keys = [
      "inspection_type",
      "site_location",
      "notes",
      // Hidden from simplified Summary tab for now:
      // "inspection_date",
      // "location",
      // "precipitation_type",
      // "precipitation_amount",
      // "weather_conditions",
      // "wind",
      // "temperature_high",
      // "temperature_low",
    ];

    return keys.some((k) => norm(editSummary?.[k]) !== norm(detail?.[k]));
  }, [editSummary, detail]);

  const handleSaveSummary = async () => {
    if (!onSaveSummary) return;
    setSummarySaveError("");
    setSummarySaved(false);
    try {
      await onSaveSummary();
      setSummarySaved(true);
      setQaQcAuditRefreshKey((value) => value + 1);
      window.setTimeout(() => setSummarySaved(false), 1800);
    } catch (err) {
      const detailMessage = err?.response?.data?.detail;
      setSummarySaveError(
        typeof detailMessage === "string" && detailMessage.trim()
          ? detailMessage
          : err?.message || "Failed to save Summary changes.",
      );
    }
  };

  return (
    <div className="space-y-3">
      {!detailLoading &&
        detail &&
        (detail.status || "").toLowerCase() === "rejected" && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <div className="text-sm font-semibold text-red-800">
              Inspection Rejected
            </div>

            <div className="mt-1 text-sm text-red-700">
              {detail.rejection_note ||
                detail.rejection_reason ||
                "No rejection reason provided."}
            </div>

            {(detail.rejected_by_name || detail.rejected_by_role) && (
              <div className="mt-2 text-xs text-red-600">
                Rejected by {detail.rejected_by_name || "Unknown user"}
                {detail.rejected_by_role ? ` (${detail.rejected_by_role})` : ""}
              </div>
            )}
          </div>
        )}
      {!detailLoading &&
        detail &&
        (detail.status || "").toLowerCase() !== "rejected" &&
        (detail.rejection_note || detail.rejection_reason) && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Previous rejection
            </div>

            <div className="mt-1 whitespace-pre-wrap text-sm text-gray-700">
              {detail.rejection_note || detail.rejection_reason}
            </div>

            {(detail.rejected_by_name || detail.rejected_by_role) && (
              <div className="mt-2 text-xs text-gray-500">
                Rejected by {detail.rejected_by_name || "Unknown user"}
                {detail.rejected_by_role ? ` (${detail.rejected_by_role})` : ""}
              </div>
            )}
          </div>
        )}
      {!detailLoading &&
        detail &&
        (detail.status || "").toLowerCase() === "submitted" &&
        isQaQcManager(projectRole) && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            QA/QC review mode: Summary wording and inspection details can be
            corrected before approval. Every changed value is recorded in the
            internal audit history below.
          </div>
        )}

      {detailLoading && (
        <div className="text-sm text-gray-500">Loading inspection…</div>
      )}

      {/*
        Rejection summary banner is hidden in the simplified Summary tab.
        Restore this area later if rejected-inspection context should appear here again.
      */}

      {!detailLoading && detail && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ["inspection_type", "text"],
              ["site_location", "text"],
              // Hidden from simplified Summary tab for now:
              // ["inspection_date", "date"],
              // ["location", "text"],
              // ["precipitation_type", "text"],
              // ["precipitation_amount", "text"],
              // ["weather_conditions", "text"],
              // ["wind", "text"],
              // ["temperature_high", "text"],
              // ["temperature_low", "text"],
            ].map(([k, type]) => (
              <div key={k}>
                <label className="text-xs text-gray-600">{fieldLabel(k)}</label>

                {k === "inspection_type" ? (
                  <div className="mt-1">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex flex-wrap gap-1">
                        {selectedTypes.length === 0 && (
                          <span className="text-xs text-gray-400">
                            No inspection types selected
                          </span>
                        )}

                        {selectedTypes.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => toggleTypeId(t.id)}
                            disabled={!summaryEditable}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-gray-300 bg-gray-50 hover:bg-gray-100 disabled:opacity-60"
                          >
                            <span>{t.name}</span>

                            {summaryEditable && (
                              <span className="text-[10px] text-gray-500">
                                ×
                              </span>
                            )}
                          </button>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => setTypesExpanded((v) => !v)}
                        className="text-xs text-gray-500 hover:text-gray-800"
                        disabled={inspectionTypesLoading}
                      >
                        {typesExpanded ? "× Hide list" : "Show list"}
                      </button>
                    </div>

                    {typesExpanded && (
                      <div className="border rounded-md">
                        <div className="px-2 py-1 border-b flex items-center gap-1 bg-gray-50">
                          <input
                            type="text"
                            value={typeSearch}
                            onChange={(e) => setTypeSearch(e.target.value)}
                            disabled={
                              !summaryEditable || inspectionTypesLoading
                            }
                            placeholder="Search inspection type…"
                            className="flex-1 text-xs bg-transparent outline-none"
                          />
                        </div>

                        <div className="max-h-40 overflow-y-auto text-sm">
                          {inspectionTypesLoading && (
                            <div className="px-3 py-2 text-xs text-gray-500">
                              Loading inspection types…
                            </div>
                          )}

                          {!inspectionTypesLoading &&
                            (inspectionTypes || []).length === 0 && (
                              <div className="px-3 py-2 text-xs text-gray-400">
                                No inspection templates defined for this
                                project.
                              </div>
                            )}

                          {!inspectionTypesLoading &&
                            (inspectionTypes || []).length > 0 &&
                            filteredTypes.length === 0 && (
                              <div className="px-3 py-2 text-xs text-gray-400">
                                No types match "{typeSearch}".
                              </div>
                            )}

                          {!inspectionTypesLoading &&
                            filteredTypes.map((t) => {
                              const checked = selectedTypeIds.includes(t.id);

                              return (
                                <label
                                  key={t.id}
                                  className="flex items-start gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    disabled={!summaryEditable}
                                    checked={checked}
                                    onChange={() => toggleTypeId(t.id)}
                                  />

                                  <div className="flex flex-col">
                                    <span>{t.name}</span>

                                    {t.description && (
                                      <span className="text-[11px] text-gray-500">
                                        {t.description}
                                      </span>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type={type}
                    disabled={!summaryEditable}
                    value={editSummary?.[k] || ""}
                    placeholder={fieldPlaceholder(k)}
                    onChange={(e) =>
                      setEditSummary((p) => ({
                        ...p,
                        [k]: e.target.value,
                      }))
                    }
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                  />
                )}
              </div>
            ))}
          </div>

          <div>
            <label className="text-xs text-gray-600">Notes</label>

            <textarea
              disabled={!summaryEditable}
              value={editSummary?.notes || ""}
              onChange={(e) =>
                setEditSummary((p) => ({
                  ...p,
                  notes: e.target.value,
                }))
              }
              rows={4}
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>

          {(qaQcAuditLoading || qaQcModifications.length > 0 ||
            ((detail.status || "").toLowerCase() === "submitted" &&
              isQaQcManager(projectRole))) && (
            <div className="rounded-lg border border-gray-200 bg-white">
              <button
                type="button"
                onClick={() => setQaQcAuditOpen((value) => !value)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-gray-800">
                    QA/QC edits
                  </span>
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                    {qaQcAuditLoading ? "…" : qaQcModifications.length}
                  </span>
                  <span className="ml-2 text-[11px] text-gray-400">
                    Internal audit only
                  </span>
                </div>
                <span className="shrink-0 text-xs font-medium text-gray-500">
                  {qaQcAuditOpen ? "Hide" : "View"}
                </span>
              </button>

              {qaQcAuditOpen && (
                <div className="border-t border-gray-100 px-3 py-2">
                  {qaQcAuditLoading ? (
                    <div className="text-xs text-gray-500">Loading edits…</div>
                  ) : qaQcModifications.length === 0 ? (
                    <div className="text-xs text-gray-500">
                      No QA/QC Summary edits recorded yet.
                    </div>
                  ) : (
                    <div className="max-h-48 space-y-1.5 overflow-y-auto">
                      {qaQcModifications.map((entry) => (
                        <div
                          key={entry.id}
                          className="rounded-md bg-gray-50 px-2.5 py-2 text-xs"
                        >
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-gray-700">
                            <span className="font-semibold">
                              {entry.actor_name || "QA/QC Manager"}
                            </span>
                            {entry.created_at ? (
                              <span className="text-gray-400">
                                {new Date(entry.created_at).toLocaleString()}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-1 space-y-0.5 text-[11px] leading-4 text-gray-600">
                            {Object.entries(entry.changes || {}).map(([field, change]) => (
                              <div key={field} className="truncate" title={`${fieldLabel(field)}: ${String(change?.before ?? "—")} → ${String(change?.after ?? "—")}`}>
                                <span className="font-medium">{fieldLabel(field)}:</span>{" "}
                                <span>{String(change?.before ?? "—")}</span>{" → "}
                                <span className="font-medium text-gray-800">
                                  {String(change?.after ?? "—")}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t space-y-3">
            {/* Inspection photo */}
            {/* <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Inspection Photos
                </div>

                <div className="text-xs text-gray-500">
                  Attached to this inspection: {photosForInspection.length}
                </div>
              </div>
            </div> */}

            {/* <PhotoUploadForm
              value={photoMeta}
              onChange={onPhotoMetaChange}
              file={photoFile}
              onFileChange={setPhotoFile}
              onSubmit={onSubmitPhoto}
              uploading={uploadingPhoto}
              submitLabel={
                uploadingPhoto ? "Uploading…" : "Upload to this Inspection"
              }
              showLinksFields={false}
            />

            <PhotoListForm
              title="Photos (this inspection)"
              photos={photosForInspection}
              loading={photosLoading}
              error={photosError}
              onDelete={removePhoto}
              thumbSizeClassName="h-10 w-10"
              getInspectionLabel={(p) =>
                detail?.inspection_number
                  ? detail.inspection_number
                  : `#${p.inspection_id || "-"}`
              }
            /> */}

            <div className="pt-4 border-t space-y-2">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Inspection PDF Documents
                </div>

                <div className="text-xs text-gray-500">
                  Upload a new PDF or link an existing PDF from Document
                  Control. Attached PDFs are available in the report modal for
                  page selection.
                </div>
              </div>

              <form
                onSubmit={onSubmitInspectionPdf}
                className="flex items-center gap-2"
              >
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm border rounded-md px-3 py-2"
                />

                <LoadingButton
                  type="submit"
                  loading={pdfUploading}
                  loadingText="Uploading…"
                  disabled={!pdfFile}
                  className="px-3 py-2 rounded-md bg-orange-500 text-white text-sm hover:bg-orange-600"
                >
                  Upload PDF
                </LoadingButton>
              </form>

              <form
                onSubmit={handleLinkExistingPdf}
                className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2"
              >
                <div>
                  <div className="text-xs font-medium text-gray-700">
                    Link existing PDF from Document Control
                  </div>
                  <div className="text-[11px] text-gray-500">
                    A Document Control PDF can be linked to more than one
                    inspection.
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={existingPdfId}
                    onChange={(e) => setExistingPdfId(e.target.value)}
                    disabled={
                      inspectionPdfsLoading || Boolean(linkingInspectionPdfId)
                    }
                    className="block w-full text-sm border rounded-md px-3 py-2 bg-white"
                  >
                    <option value="">
                      {availableInspectionPdfs.length
                        ? "Select an existing PDF…"
                        : "No additional PDFs available"}
                    </option>
                    {availableInspectionPdfs.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.document_name || `Document ${doc.id}`}
                        {doc.category ? ` · ${doc.category}` : ""}
                        {Array.isArray(doc.inspection_numbers) &&
                        doc.inspection_numbers.length
                          ? ` · Linked: ${doc.inspection_numbers.join(", ")}`
                          : ""}
                      </option>
                    ))}
                  </select>

                  <LoadingButton
                    type="submit"
                    loading={Boolean(linkingInspectionPdfId)}
                    loadingText="Linking…"
                    disabled={!existingPdfId || Boolean(linkingInspectionPdfId)}
                    className="px-3 py-2 rounded-md bg-gray-900 text-white text-sm whitespace-nowrap disabled:opacity-50"
                  >
                    Link PDF
                  </LoadingButton>
                </div>
              </form>

              <div className="rounded-md border border-gray-200">
                <div className="px-3 py-2 border-b text-sm font-medium text-gray-800">
                  PDFs (this inspection)
                </div>

                {inspectionPdfsLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Loading PDFs…
                  </div>
                )}

                {!inspectionPdfsLoading && inspectionPdfs.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No PDFs attached to this inspection.
                  </div>
                )}

                {!inspectionPdfsLoading &&
                  inspectionPdfs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between px-3 py-2 border-t"
                    >
                      <div className="text-sm truncate">
                        {doc.document_name || `PDF #${doc.id}`}
                      </div>

                      <div className="flex gap-2">
                        {doc.file_url && (
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 text-xs underline"
                          >
                            Open
                          </a>
                        )}

                        <button
                          type="button"
                          onClick={() => onUnlinkInspectionPdf?.(doc.id)}
                          className="text-red-600 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <LoadingButton
              loading={savingSummary}
              loadingText="Saving…"
              disabled={!summaryEditable || !summaryDirty}
              onClick={handleSaveSummary}
              className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm"
            >
              Save
            </LoadingButton>

            {!summaryEditable && (
              <div className="text-xs text-gray-500 self-center">
                Summary is read-only for this status or role.
              </div>
            )}
            {summarySaved && (
              <div className="self-center text-xs font-medium text-emerald-700">
                Saved
              </div>
            )}
          </div>
          {summarySaveError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {summarySaveError}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(SummaryTab);
